import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { scroller } from 'react-scroll';

import PriceDisplay from '../../../../components/global/PriceDisplay';
import Button from '../../../../components/global/Button';
import getConfig from '../../../../util/config';
import { getAsset } from '../../../../util/assets';
import { gtm } from '../../../../util/analytics';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../hooks/useOpenModal';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    addresses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
  }),
  loading: PropTypes.bool,
  finalPrice: PropTypes.number,
  totalPrice: PropTypes.number,
  onStripeToken: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  blockers: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      condition: PropTypes.bool.isRequired,
    }),
  ),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  checkoutItems: PropTypes.object,
  allowedPromoCodes: PropTypes.array,
  stripeModalProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
};

const CheckoutCartPaymentPanel = ({
  location,
  account,
  loading,
  finalPrice,
  totalPrice,
  onStripeToken,
  submitting,
  blockers,
  checkoutItems,
  allowedPromoCodes = [],
  stripeModalProps = {},
}) => {
  const openModal = useOpenModal();
  const [renderBlocker, setRenderBlocker] = useState(false);
  const [blocker] = blockers;
  const { baseUrl } = getConfig()?.app ?? {};

  return (
    <div className="checkout-cart-payment-panel">
      <div className="checkout-cart-payment-panel-price">
        {finalPrice > 0 && (
          <Fragment>
            <span>Total</span>
            <PriceDisplay
              price={finalPrice}
              oldPrice={totalPrice}
              showDiscount
              animatedStrikethrough
              loading={loading}
            />
          </Fragment>
        )}
      </div>

      <div className="ml-auto text-right">
        {blocker && renderBlocker && (
          <p className="text-sm text-danger mb-1">{blocker.message}</p>
        )}

        <Button
          data-testid="checkout-cart-payment-panel-submit"
          className={classNames({
            'checkout-cart-payment-panel-submit': true,
            'is-loading': loading || submitting,
            disabled: !!blocker,
          })}
          disabled={loading || submitting}
          onClick={event => {
            setRenderBlocker(false);

            // If there's a blocker condition (meaning the user should not check out)
            // prevent check out and show the blocker's message.
            if (blocker) {
              setRenderBlocker(true);

              if (blocker.scrollElement) {
                scroller.scrollTo(blocker.scrollElement, {
                  duration: 500,
                  smooth: true,
                  offset: 50,
                });
              }

              event.preventDefault();
              event.stopPropagation();
              return;
            }

            gtm?.trackPageView({
              url: `${baseUrl}${location.pathname}/virtual/payment`,
              title: 'Virtual Pageview - Payment',
              userId: account?.id,
            });

            openModal(STRIPE_CHECKOUT_MODAL, {
              title: (
                <h3 className="d-flex w-100 justify-content-center align-items-center mb-0">
                  <img
                    src={getAsset('/waldo-stripe-logo.png')}
                    alt="Waldo"
                    width="48px"
                    className="mr-1"
                  />
                  Waldo Photos
                </h3>
              ),
              description: <p className="text-center">Checkout</p>,
              onSuccess: onStripeToken,
              cardRequired: false,
              accountReferralTypes: allowedPromoCodes,
              checkoutPriceVariables: ({ promo_code: promoCode }) => ({
                ...(checkoutItems ?? {}),
                account_id: account?.id,
                address_id: account?.addresses?.[0]?.id,
                promo_code: promoCode,
              }),
              defaultName: account?.name ?? '',
              defaultEmail: account?.email_address ?? '',
              ...stripeModalProps,
            });
          }}
        >
          {loading || submitting ? 'Loading…' : 'Continue'}
        </Button>
      </div>
    </div>
  );
};

CheckoutCartPaymentPanel.propTypes = propTypes;

export default CheckoutCartPaymentPanel;
